import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import MermaidDiagram from './MermaidDiagram';
import { matchMemberName } from './mentions';
import { TYPE } from '../../constants/typeScale';

/** True when a code node is a ```mermaid fenced block. */
const isMermaid = (className?: unknown): boolean =>
  typeof className === 'string' && /\blanguage-mermaid\b/.test(className);

/** Plain-text content of a react-markdown node's children (for name matching in a bold run). */
const textOf = (children: React.ReactNode): string =>
  React.Children.toArray(children).map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : '')).join('');

/**
 * Renders a specialist message as Markdown — headings, lists, bold, code,
 * tables (GFM), and LaTeX math (KaTeX) — in a compact, monochrome chat style.
 * Math: inline `$…$` and block `$$…$$` render as typeset formulas, so the
 * science/quant/maths agents' answers (derivatives, sums, fractions) display
 * properly instead of raw `$F(x)$`. Other structured answers (charts/tables)
 * come through as json-render UISpecs.
 */
const Markdown: React.FC<{
  content: string;
  /** Roster names — a bold run matching one becomes a clickable mention. */
  memberNames?: string[];
  /** Clicking a matched agent name inserts its `@handle` into the composer. */
  onMention?: (name: string) => void;
}> = ({ content, memberNames, onMention }) => (
  <div
    data-testid="markdown"
    className={[
      // A specialist reply IS the chat body, so it INHERITS the bubble's deliberate 13px (owner 2026-08-22)
      // rather than declaring a size of its own. A `text-sm` here rendered every agent reply one step larger
      // than the user's own bubble sitting right beside it — the bulk the right pane was showing.
      'leading-relaxed space-y-2 break-words',
      // A heading inside a bubble is a SECTION heading, never a page title: the pane is a 272px rail and the
      // scale keeps its one title step for the single title on a page. Weight carries h1 through h3.
      //
      // WRITTEN OUT, NOT INTERPOLATED (BUGS.md 2026-08-24). `[&_h1]:${TYPE.sectionTitle}` looks right and is
      // not: the role is TWO classes, so only the first gets the `[&_h1]:` prefix and the second — a bare
      // `font-semibold` — lands on this WRAPPER. Every specialist reply rendered bold because of it. A
      // multi-class token can never be dropped into an arbitrary variant; each class needs its own prefix.
      '[&_h1]:text-[13px] [&_h1]:font-semibold',
      '[&_h2]:text-[13px] [&_h2]:font-semibold',
      '[&_h3]:text-[13px] [&_h3]:font-semibold',
      '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-0.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-0.5',
      '[&_a]:underline [&_a]:font-medium',
      '[&_strong]:font-semibold',
      `[&_code]:rounded [&_code]:bg-secondary-200 dark:[&_code]:bg-secondary-700 [&_code]:px-1 [&_code]:py-0.5 [&_code]:${TYPE.control}`,
      `[&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-secondary-900 dark:[&_pre]:bg-black [&_pre]:p-3 [&_pre]:${TYPE.control}`,
      '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-secondary-100',
      `[&_table]:w-full [&_table]:${TYPE.control} [&_th]:text-left [&_th]:font-semibold [&_th]:border-b [&_th]:border-secondary-300 dark:[&_th]:border-secondary-600 [&_th]:py-1 [&_th]:pr-3`,
      '[&_td]:border-b [&_td]:border-secondary-200 dark:[&_td]:border-secondary-600 [&_td]:py-1 [&_td]:pr-3',
      '[&_blockquote]:border-l-2 [&_blockquote]:border-secondary-300 dark:[&_blockquote]:border-secondary-600 [&_blockquote]:pl-3 [&_blockquote]:text-secondary-500',
      // Block math (display) can be wide — let it scroll instead of overflowing the bubble.
      '[&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:py-1',
    ].join(' ')}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      components={{
        a: ({ node, children, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>,
        // An agent naming another agent renders as **bold**; if the bold run IS a roster name, turn it
        // into a clickable mention that drops the @handle into the composer (still bold, now underlined).
        strong: ({ node, children, ...props }: any) => {
          const name = onMention && memberNames?.length ? matchMemberName(textOf(children), memberNames) : null;
          if (name) {
            return (
              <button type="button" onClick={() => onMention!(name)}
                className="font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid"
                title={`Mention ${name}`}>{children}</button>
            );
          }
          return <strong {...props}>{children}</strong>;
        },
        // A ```mermaid block renders as a diagram (lazy-loaded); other code is unchanged.
        code: ({ node, className, children, ...props }: any) =>
          isMermaid(className)
            ? <MermaidDiagram code={String(children).replace(/\n$/, '')} />
            : <code className={className} {...props}>{children}</code>,
        // Don't wrap a mermaid diagram in the dark code-block <pre> chrome.
        pre: ({ node, children, ...props }: any) =>
          isMermaid(node?.children?.[0]?.properties?.className)
            ? <>{children}</>
            : <pre {...props}>{children}</pre>,
      }}
    >
      {content}
    </ReactMarkdown>
  </div>
);

export default Markdown;
